import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui';
import { StandardIcon } from '@/components/common/icon';
import { useApiWithAuth } from '@/hooks/api';
import { useAuth } from '@/hooks/auth';
import type { ActAsUserOption } from '@/shared/types/auth';
import { clearActAsUserId, isBetaClient, setActAsUserId } from '@/lib/actAs';

function reloadWithActAsUser(userId: string | null) {
  if (userId) {
    setActAsUserId(userId);
  } else {
    clearActAsUserId();
  }

  window.location.reload();
}

export default function ActAsDialog({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const auth = useAuth();
  const { api } = useApiWithAuth();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [users, setUsers] = useState<ActAsUserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enabled =
    isBetaClient() &&
    Boolean(auth.authorized) &&
    Boolean(auth.actorMetadata?.data.admin);

  useEffect(() => {
    if (!enabled || auth.actingAsUserId || !isOpen || !auth.firebaseUser) {
      return () => {};
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void apiRef
      .current('/auth/act-as/users', auth.firebaseUser, {})
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Unable to load users for access preview.');
        }

        const payload = (await response.json()) as ActAsUserOption[];
        if (!cancelled) {
          setUsers(payload);
        }

        return undefined;
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error
              ? reason.message
              : 'Unable to load users for access preview.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.actingAsUserId, auth.firebaseUser, enabled, isOpen]);

  const availableUsers = useMemo(
    () => users.filter((user) => user.id !== auth.actorMetadata?.id),
    [auth.actorMetadata?.id, users],
  );

  if (!enabled || auth.actingAsUserId) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} placement='top'>
      <ModalContent size='sm'>
        <ModalHeader>Preview user access</ModalHeader>
        <ModalBody>
          <p className='text-sm text-slate-500'>
            Search for an active user to see the app as they do.
          </p>
          <Autocomplete
            className='w-full text-slate-950'
            items={availableUsers}
            inputValue={query}
            onInputChange={setQuery}
            selectedKey={selectedUserId}
            onSelectionChange={(key) =>
              setSelectedUserId(key ? String(key) : null)
            }
            isLoading={loading}
            placeholder='Search by name or email…'
            aria-label='Select a user to act as'
            listboxProps={{
              portal: true,
              emptyContent: error || 'No active linked users found',
            }}
          >
            {(user) => (
              <AutocompleteItem
                key={user.id}
                textValue={`${user.displayName} ${user.email}`}
              >
                <div className='flex min-w-0 flex-col'>
                  <span className='truncate font-medium'>
                    {user.displayName}
                  </span>
                  <span className='truncate text-xs text-slate-500'>
                    {user.email || 'No email'}
                  </span>
                </div>
              </AutocompleteItem>
            )}
          </Autocomplete>
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className='bg-[#ff531d] font-semibold text-white shadow-sm hover:bg-[#ef4612]'
            isDisabled={!selectedUserId}
            onPress={() =>
              selectedUserId && reloadWithActAsUser(selectedUserId)
            }
            startContent={<StandardIcon id='mdi:account-switch' />}
          >
            Act as user
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
